/**
 * ユーザー管理ページ — Stage 2C T9 真实化版
 *
 * 数据源:GET /api/admin/users
 * 操作:invite(POST /admin/users/invite) / enable / disable / patch(name + role_code)
 *
 * 约束:
 * - PATCH 只允许 name / role_code 后端有校验,前端不发送其他字段
 * - invite 后端用 invitation_codes.note 暂存 email,避免同邮箱重复发码
 * - 非 system_admin 由路由守卫拦截,本页不再做角色检查
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Shield,
  Edit3,
  UserMinus,
  UserCheck,
  Search,
  RefreshCw,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Table, TableColumn } from "../../components/ui/Table";
import { ApiError } from "../../api/client";
import {
  adminApi,
  type AdminUser,
  type RoleCode,
} from "../../api/admin";
import { formatJstDateTime } from "../../utils/dateTime";

// 后端 role_code → 日本語表示
const ROLE_LABEL: Record<RoleCode, string> = {
  system_admin: "管理者",
  editor: "編集者",
  viewer: "閲覧者",
};
const ROLE_COLOR: Record<RoleCode, "blue" | "green" | "gray"> = {
  system_admin: "blue",
  editor: "green",
  viewer: "gray",
};

const ROLE_OPTIONS: RoleCode[] = ["system_admin", "editor", "viewer"];

function statusBadgeColor(status: string): "green" | "red" | "yellow" {
  if (status === "active") return "green";
  if (status === "disabled") return "red";
  return "yellow";
}

function statusLabel(status: string): string {
  if (status === "active") return "アクティブ";
  if (status === "disabled") return "停止中";
  return status;
}

export const UsersPage = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 検索 / フィルタ
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | RoleCode>("all");

  // 現在操作中の user id(行内ボタン disabled 用)
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.listUsers({
        page: 1,
        page_size: 100,
        email: activeSearch || undefined,
        role_code: roleFilter === "all" ? undefined : roleFilter,
      });
      setUsers(data.items);
      setTotal(data.total);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [activeSearch, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // 集計(roleDescriptions 代替)
  const roleCounts = useMemo(() => {
    const c: Record<RoleCode, number> = { system_admin: 0, editor: 0, viewer: 0 };
    for (const u of users) {
      for (const rc of u.role_codes) {
        c[rc] = (c[rc] ?? 0) + 1;
      }
    }
    return c;
  }, [users]);

  const activeCount = useMemo(
    () => users.filter((u) => u.status === "active").length,
    [users]
  );

  // ---- handlers ----

  async function handleDisable(user: AdminUser) {
    if (!window.confirm(`${user.email} を停止しますか?\n停止すると、そのユーザーの access_token は全て無効化されます。`)) {
      return;
    }
    setBusyId(user.id);
    try {
      await adminApi.disableUser(user.id);
      fetchUsers();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      window.alert(`停止に失敗しました: ${msg}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleEnable(user: AdminUser) {
    setBusyId(user.id);
    try {
      await adminApi.enableUser(user.id);
      fetchUsers();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      window.alert(`有効化に失敗しました: ${msg}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleEdit(user: AdminUser) {
    const newName = window.prompt(`${user.email} の新しい名前を入力してください`, user.name);
    if (newName === null) return; // cancel
    const trimmedName = newName.trim();
    if (!trimmedName) {
      window.alert("名前は空にできません。");
      return;
    }
    if (trimmedName.length > 100) {
      window.alert("名前は 100 文字以内にしてください。");
      return;
    }

    const currentRole = (user.role_codes[0] ?? "viewer") as RoleCode;
    const newRoleRaw = window.prompt(
      `${user.email} の新しいロールコードを入力してください (${ROLE_OPTIONS.join(" / ")})`,
      currentRole
    );
    if (newRoleRaw === null) return;
    const newRole = newRoleRaw.trim() as RoleCode;
    if (!ROLE_OPTIONS.includes(newRole)) {
      window.alert(`ロールコードは ${ROLE_OPTIONS.join(" / ")} のいずれかを指定してください。`);
      return;
    }

    setBusyId(user.id);
    try {
      // PATCH 只发送 name / role_code
      await adminApi.patchUser(user.id, { name: trimmedName, role_code: newRole });
      fetchUsers();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      window.alert(`更新に失敗しました: ${msg}`);
    } finally {
      setBusyId(null);
    }
  }

  function handleSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      setActiveSearch(searchInput.trim());
    }
  }

  // ---- table columns ----

  const columns: TableColumn<AdminUser>[] = [
    {
      key: "name",
      header: "名前",
      render: (u) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="avatar">{u.name.charAt(0)}</div>
          <div>
            <div className="table-cell-title">{u.name}</div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "メールアドレス",
      render: (u) => (
        <span className="table-cell-muted" style={{ fontFamily: "Menlo, monospace", fontSize: 12 }}>
          {u.email}
        </span>
      ),
    },
    {
      key: "role",
      header: "権限",
      width: 140,
      render: (u) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {u.role_codes.length === 0 ? (
            <Badge color="gray">未割当</Badge>
          ) : (
            u.role_codes.map((rc) => (
              <Badge key={rc} color={ROLE_COLOR[rc]}>
                {ROLE_LABEL[rc]}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      key: "lastLogin",
      header: "最終ログイン",
      width: 200,
      render: (u) => (
        <span className="table-cell-muted">{formatJstDateTime(u.last_login_at)}</span>
      ),
    },
    {
      key: "status",
      header: "ステータス",
      width: 130,
      render: (u) => (
        <Badge color={statusBadgeColor(u.status)} dot>
          {statusLabel(u.status)}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "操作",
      align: "right",
      width: 180,
      render: (u) => {
        const isBusy = busyId === u.id;
        const isActive = u.status === "active";
        return (
          <div className="table-actions">
            <button
              className="btn-icon"
              onClick={() => handleEdit(u)}
              disabled={isBusy}
              title="編集(name / role_code)"
            >
              <Edit3 size={14} />
            </button>
            {isActive ? (
              <button
                className="btn-icon btn-icon-danger"
                onClick={() => handleDisable(u)}
                disabled={isBusy}
                title="無効化"
              >
                <UserMinus size={14} />
              </button>
            ) : (
              <button
                className="btn-icon"
                onClick={() => handleEnable(u)}
                disabled={isBusy}
                title="有効化"
              >
                <UserCheck size={14} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // ---- role description cards (Stage 1 mock を role_counts に置き換え) ----

  const roleDescriptions: Array<{
    title: string;
    badge: "blue" | "green" | "gray";
    description: string;
    count: number;
  }> = [
    {
      title: "管理者",
      badge: "blue",
      description: "すべての機能にアクセス可能。ユーザー管理・設定を含む全権限を持ちます。",
      count: roleCounts.system_admin,
    },
    {
      title: "編集者",
      badge: "green",
      description: "ブログ・メディア・メール・チャットボット・サイトコンテンツの編集と公開が可能です。",
      count: roleCounts.editor,
    },
    {
      title: "閲覧者",
      badge: "gray",
      description: "ダッシュボードやレポートの閲覧のみ可能。データの編集や公開はできません。",
      count: roleCounts.viewer,
    },
  ];

  return (
    <div>
      <div className="grid-12 mb-18">
        {roleDescriptions.map((r) => (
          <div className="col-4" key={r.title}>
            <Card>
              <div className="row-between mb-12">
                <div className="row gap-12">
                  <div className="settings-card-icon">
                    <Shield size={18} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#111827",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {r.title}
                      <Badge color={r.badge}>{r.count} 名</Badge>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.7 }}>{r.description}</div>
            </Card>
          </div>
        ))}
      </div>

      <Card
        title="ユーザー一覧"
        subtitle={
          loading
            ? "読み込み中..."
            : error
              ? `エラー: ${error}`
              : `登録 ${total} 名 / アクティブ ${activeCount} 名`
        }
        actions={
          <div className="row gap-8">
            <div className="input-with-icon" style={{ width: 280 }}>
              <span className="input-icon">
                <Search size={14} />
              </span>
              <input
                className="input"
                placeholder="メールアドレス検索（Enterで実行）"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKey}
              />
            </div>
            <select
              className="select"
              style={{ width: 130 }}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as "all" | RoleCode)}
            >
              <option value="all">全権限</option>
              <option value="system_admin">管理者</option>
              <option value="editor">編集者</option>
              <option value="viewer">閲覧者</option>
            </select>
            <Button
              iconLeft={<RefreshCw size={14} />}
              onClick={fetchUsers}
              disabled={loading}
            >
              再読み込み
            </Button>
          </div>
        }
        noPadding
      >
        <Table
          columns={columns}
          data={users}
          rowKey={(u) => String(u.id)}
          emptyText={loading ? "読み込み中..." : error ? "取得に失敗しました" : "ユーザーがいません"}
        />
      </Card>
    </div>
  );
};
